<template>
  <div class="Timeline" :class="[`Timeline--${variant}`, { 'Timeline--hasMore': hasMore }]">
    <div
      v-for="(section, sectionIndex) of sections"
      :key="section.label ?? sectionIndex"
      class="Timeline-section"
      :class="{ 'Timeline-section--upcoming': section.upcoming }"
    >
      <div class="Timeline-marker">
        <div class="Timeline-markerBullet">
          <slot name="marker" :section="section" :index="sectionIndex">
            <Icon icon="circle" :size="markerSize" class="Timeline-markerIcon" />
          </slot>
        </div>
      </div>

      <div class="Timeline-sectionContent">
        <span class="Timeline-label">{{ sectionLabel(section) }}</span>

        <div
          v-for="(item, itemIndex) of section.items"
          :key="item[itemKey] as PropertyKey || itemIndex"
          class="Timeline-item"
        >
          <slot name="item" :item="item" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends object">
import { computed } from 'vue';
import { format } from 'date-fns';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Icon from '@cvo/ui/components/atoms/Icon/Icon.vue';
import { useResponsive } from '@cvo/ui/composables';
import type { TimelineProps, TimelineSection } from './Timeline.model';

const props = withDefaults(defineProps<TimelineProps<T>>(), {
  hasMore: false,
  variant: 'secondary',
});

const { t } = useI18n();

function formatDate(date: Date | string | number): string {
  return format(new Date(date), props.dateFormat ?? t('ui.components.timeline.dateFormat'));
}

function sectionLabel(section: TimelineSection<T>): string {
  const parts = [
    section.dateStart ? formatDate(section.dateStart) : null,
    section.dateEnd ? formatDate(section.dateEnd) : null,
    section.label,
  ].filter(Boolean);
  return parts.join(t('ui.components.timeline.separator'));
}

const MARKER_SIZE_MOBILE = 8;
const MARKER_SIZE_DESKTOP = 16;
const { isMobile } = useResponsive();
const markerSize = computed(() => (isMobile.value ? MARKER_SIZE_MOBILE : MARKER_SIZE_DESKTOP));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

$timeline-outer-circle-size-mobile: vars.$dimensions-d-4;
$timeline-outer-circle-size-desktop: vars.$dimensions-d-6;

@layer cvo-ui {
  .Timeline {
    display: flex;
    flex-direction: column;

    &-section {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      gap: vars.$dimensions-d-3;
    }

    &-sectionContent {
      display: flex;
      flex-direction: column;
      flex: 1;
      padding-block-end: vars.$dimensions-d-2;
    }

    &-item {
      display: flex;
      flex-direction: column;
      padding-top: vars.$dimensions-d-2;
    }

    &-label {
      @include text.text('md', vars.$typography-weight-strong);
      color: vars.$organisms-timeline-event-title-color;
      display: flex;
      align-items: center;
      min-height: $timeline-outer-circle-size-mobile;

      @include responsive.tablet {
        min-height: $timeline-outer-circle-size-desktop;
      }
    }

    // Neutral fallback colours, overridden by each variant
    &-markerIcon {
      color: vars.$color-neutral-gray-700;
      flex-shrink: 0;
    }

    &-markerBullet {
      width: $timeline-outer-circle-size-mobile;
      height: $timeline-outer-circle-size-mobile;
      border-radius: 50%;
      background-color: vars.$color-neutral-gray-300;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      @include responsive.tablet {
        width: $timeline-outer-circle-size-desktop;
        height: $timeline-outer-circle-size-desktop;
      }
    }

    &-marker {
      display: flex;
      flex-direction: column;
      align-items: center;
      flex-shrink: 0;
      width: $timeline-outer-circle-size-mobile;

      @include responsive.tablet {
        width: $timeline-outer-circle-size-desktop;
      }

      &::after {
        content: '';
        width: vars.$dimensions-d-1;
        flex: 1;
        background-color: vars.$color-neutral-gray-300;
      }
    }

    &--primary {
      // Keeps the desktop marker size on mobile
      .Timeline-marker,
      .Timeline-markerBullet {
        width: $timeline-outer-circle-size-desktop;
      }

      .Timeline-markerBullet {
        height: $timeline-outer-circle-size-desktop;
        background-color: vars.$organisms-timeline-event-primary-completed-bg-color;
      }

      .Timeline-label {
        min-height: $timeline-outer-circle-size-desktop;
      }

      .Timeline-section {
        @include responsive.mobile {
          min-height: vars.$dimensions-d-20;
        }
      }

      .Timeline-sectionContent {
        padding-block-end: vars.$dimensions-d-3;
      }

      .Timeline-markerIcon {
        color: vars.$organisms-timeline-event-primary-completed-icon-color;
      }

      .Timeline-marker::after {
        background-color: vars.$organisms-timeline-event-primary-completed-connector-color;
      }

      // The current section is the last enabled one before the upcoming ones
      .Timeline-section:not(.Timeline-section--upcoming):has(+ .Timeline-section--upcoming) {
        .Timeline-markerBullet {
          background-color: vars.$organisms-timeline-event-primary-current-bg-color;
        }

        .Timeline-markerIcon {
          color: vars.$organisms-timeline-event-primary-current-icon-color;
        }

        .Timeline-marker::after {
          background-color: vars.$organisms-timeline-event-primary-current-connector-color;
        }
      }
    }

    &--secondary {
      .Timeline-markerBullet,
      .Timeline-marker::after {
        background-color: vars.$organisms-timeline-event-secondary-bg-color;
      }

      .Timeline-markerIcon {
        color: vars.$organisms-timeline-event-secondary-icon-color;
      }
    }

    // Declared after the variants so it wins over them
    &-section--upcoming {
      .Timeline-markerBullet {
        background-color: vars.$organisms-timeline-event-primary-upcoming-bg-color;
      }

      .Timeline-markerIcon {
        color: vars.$organisms-timeline-event-primary-upcoming-icon-color;
      }

      .Timeline-marker::after {
        background-color: vars.$organisms-timeline-event-primary-upcoming-connector-color;
      }

      .Timeline-label {
        color: vars.$organisms-timeline-event-primary-upcoming-title-color;
      }
    }

    &-section:has(+ &-section--upcoming) > .Timeline-marker::after {
      background-color: vars.$organisms-timeline-event-primary-upcoming-connector-color;
    }

    // Hide trail on the last section's marker (no more sections)
    &:not(&--hasMore) &-section:last-child > .Timeline-marker::after {
      display: none;
    }

    // Fade trail on the last section's marker, whatever its colour
    &-section:last-child > .Timeline-marker::after {
      mask-image: linear-gradient(180deg, black 50%, transparent 100%);
    }
  }
}
</style>
